//External
import React from "react";
//Internal
import { getSwatchColor } from "./colorSwatches";
import FiltersDialog from "./FiltersDialog";
import MultiSelectFilterDropdown from "./MultiSelectFilterDropdown";
import PriceRangeFilterDropdown from "./PriceRangeFilterDropdown";
import SeatingCapacityFilterDropdown from "./SeatingCapacityFilterDropdown";

/*******************
@Purpose : Search results filter row (Categories/Brands/Price Range/Seating
           Capacity/Colors pills, plus a funnel icon that opens the
           all-in-one advanced Filters dialog). Below $breakpoint-lg the
           funnel icon is hidden here (CSS) since MobileHeader's own funnel
           button opens the same dialog instead - isFiltersDialogOpen is
           therefore owned by the page, not this component, so both triggers
           control one shared piece of state. Every option count and
           zero-result option hiding is computed instantly off `pool` (the
           caller's single availability fetch for the current location+
           dates) rather than each dropdown firing its own request.
@Parameter : {..., pool, isFiltersDialogOpen, setIsFiltersDialogOpen}
@Author : INIC
******************/
function ResultsFilters({
  categoryList,
  brandList,
  colorList,
  priceBounds,
  selectedCategories,
  setSelectedCategories,
  selectedBrands,
  setSelectedBrands,
  selectedColors,
  setSelectedColors,
  selectedSeats,
  setSelectedSeats,
  priceRange,
  setPriceRange,
  pool,
  isFiltersDialogOpen,
  setIsFiltersDialogOpen,
  baseFilters = {},
  hiddenFilters = [],
  alwaysShowFilterIcon = false,
}) {
  const isHidden = (name) => hiddenFilters.includes(name);
  const isAnyFilterActive =
    selectedCategories.length > 0 ||
    selectedBrands.length > 0 ||
    selectedColors.length > 0 ||
    selectedSeats.length > 0 ||
    priceRange.min != null ||
    priceRange.max != null;

  /*******************
  @purpose : clear every applied filter at once - unlike the individual pill
             dropdowns/Filters dialog, these setters own the applied values
             directly here, so there's no draft/apply step to go through
  @Author : INIC
  ******************/
  const handleResetAll = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedColors([]);
    setSelectedSeats([]);
    setPriceRange({ min: null, max: null });
  };

  return (
    <div
      className={`results-filters ${
        alwaysShowFilterIcon ? "results-filters--with-icon" : ""
      }`}
    >
      {!isHidden("categories") && (
        <MultiSelectFilterDropdown
          label="Categories"
          optionList={categoryList}
          selectedValues={selectedCategories}
          setSelectedValues={setSelectedCategories}
          filterKey="categorySlugs"
          otherFilters={{
            brandSlugs: selectedBrands,
            minPrice: priceRange.min ?? undefined,
            maxPrice: priceRange.max ?? undefined,
            seats: selectedSeats,
            colorIds: selectedColors,
            ...baseFilters,
          }}
          pool={pool}
          colorList={colorList}
        />
      )}

      {!isHidden("brands") && (
        <MultiSelectFilterDropdown
          label="Brands"
          optionList={brandList}
          selectedValues={selectedBrands}
          setSelectedValues={setSelectedBrands}
          filterKey="brandSlugs"
          otherFilters={{
            categorySlugs: selectedCategories,
            minPrice: priceRange.min ?? undefined,
            maxPrice: priceRange.max ?? undefined,
            seats: selectedSeats,
            colorIds: selectedColors,
            ...baseFilters,
          }}
          pool={pool}
          colorList={colorList}
        />
      )}

      <PriceRangeFilterDropdown
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        otherFilters={{
          categorySlugs: selectedCategories,
          brandSlugs: selectedBrands,
          seats: selectedSeats,
          colorIds: selectedColors,
          ...baseFilters,
        }}
        pool={pool}
        colorList={colorList}
      />

      <SeatingCapacityFilterDropdown
        selectedSeats={selectedSeats}
        setSelectedSeats={setSelectedSeats}
        otherFilters={{
          categorySlugs: selectedCategories,
          brandSlugs: selectedBrands,
          minPrice: priceRange.min ?? undefined,
          maxPrice: priceRange.max ?? undefined,
          colorIds: selectedColors,
          ...baseFilters,
        }}
        pool={pool}
        colorList={colorList}
      />

      <MultiSelectFilterDropdown
        label="Colors"
        optionList={colorList}
        selectedValues={selectedColors}
        setSelectedValues={setSelectedColors}
        filterKey="colorIds"
        getOptionValue={(color) => color.id}
        getOptionLabel={(color) => color.name}
        getOptionSwatch={(color) => color.code || getSwatchColor(color.name)}
        otherFilters={{
          categorySlugs: selectedCategories,
          brandSlugs: selectedBrands,
          minPrice: priceRange.min ?? undefined,
          maxPrice: priceRange.max ?? undefined,
          seats: selectedSeats,
          ...baseFilters,
        }}
        pool={pool}
        colorList={colorList}
      />

      {isAnyFilterActive && (
        <button type="button" className="filter-reset-btn" onClick={handleResetAll}>
          Clear
        </button>
      )}

      <button
        type="button"
        className="results-filters-icon"
        onClick={() => setIsFiltersDialogOpen(true)}
        aria-label="All filters"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          role="presentation"
          focusable="false"
        >
          <g clipPath="url(#clip0_800_12964)">
            <path d="M16.6673 3.33374H3.33398V5.18957C3.33403 5.60407 3.48852 6.00368 3.76732 6.31041L7.50065 10.4171V17.5004L12.5007 15.8337V10.0004L16.179 6.32207C16.4916 6.00959 16.6672 5.58573 16.6673 5.14374V3.33374Z" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <defs>
            <clipPath id="clip0_800_12964">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>

      <FiltersDialog
        isOpen={isFiltersDialogOpen}
        onClose={() => setIsFiltersDialogOpen(false)}
        categoryList={categoryList}
        brandList={brandList}
        colorList={colorList}
        priceBounds={priceBounds}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        selectedBrands={selectedBrands}
        setSelectedBrands={setSelectedBrands}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        selectedSeats={selectedSeats}
        setSelectedSeats={setSelectedSeats}
        selectedColors={selectedColors}
        setSelectedColors={setSelectedColors}
        pool={pool}
        baseFilters={baseFilters}
        hiddenFilters={hiddenFilters}
      />
    </div>
  );
}

export default ResultsFilters;
